// src/utils/invoicePDFGenerator.js
import html2pdf from "html2pdf.js";
import { createB2BAPI } from "../api/Interceptor";

const generateTableRows = (order, items) => {
  if (!Array.isArray(items)) return "";

  const isDeliveredNonSwatch =
    order?.orderStatus === "DELIVERED" &&
    order?.orderType !== "SWATCH" &&
    order?.orderType !== "SAMPLE";

  const isWholesale = order?.orderType === "WHOLESALE";
  const isSwatch = order?.orderType === "SWATCH";

  return items
    .map((row, index) => {
      const productName = isSwatch
        ? (row.productName || "")
        : (row.productVariant?.name || row.productName || "");

      const quantity = isWholesale
        ? Math.round(row.orderedRolls || 0)
        : (row.qty || 0);

      const qtyPerRoll = isWholesale ? (row.stdKg || "") : "";

      const sellPriceDisplay = isSwatch
        ? `${row.sellingPrice} points`
        : `₹${row.sellingPrice}`;

      const totalAmt = (row.totalAmount || 0).toFixed(2);

      return `
        <tr>
          ${isDeliveredNonSwatch ? `<td class="inv_td" style="text-align:center; width:6%;">${index + 1}</td>` : ""}
          
          <td class="inv_td" style="font-weight:500;">${productName}</td>
          
          <td class="inv_td" style="text-align:center;">${quantity}</td>
          
          <td class="inv_td" style="text-align:center;">${row.uom || ""}</td>
          
          ${isWholesale ? `<td class="inv_td" style="text-align:center;">${qtyPerRoll}</td>` : ""}
          
          <td class="inv_td" style="text-align:center;">${sellPriceDisplay}</td>
          
          <td class="inv_td" style="text-align:center; font-weight:600;">₹${totalAmt}</td>
        </tr>
      `;
    })
    .join("");
};

const getGrossTotal = (items) => {
  if (!Array.isArray(items)) return "0.00";
  const total = items.reduce((acc, item) => acc + (item.totalAmount || 0), 0);
  return total.toFixed(2);
};

export const generateInvoiceHTML = (order, companys) => {
  const isDeliveredNonSwatch =
    order?.orderStatus === "DELIVERED" &&
    order?.orderType !== "SWATCH" &&
    order?.orderType !== "SAMPLE";

  const isWholesale = order?.orderType === "WHOLESALE";
  const isSwatch = order?.orderType === "SWATCH";

  const right = [
    { field: "Date", value: new Date().toLocaleDateString() },
    { field: "Customer ID", value: order?.customer?.customerId || "N/A" },
    { field: "Order NO", value: order?.orderNo || "N/A" },
    { field: "DC NO", value: order?.receiptNo || "N/A" },
    { field: "Invoice NO", value: order?.invoice?.invoiceNo || "N/A" },
  ];

  const bodyLeftSection = [ /* ... same as before ... */];
  const bodyRightSection = [ /* ... same as before ... */];

  const company = { /* ... same as before ... */ };

  const grossTotal = getGrossTotal(order?.items);
  const tableRows = generateTableRows(order, order?.items);

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Invoice</title>
  <style>
    .inv_body {
      font-family: Arial, sans-serif;
      padding: 20px;
      margin: 0;
      background: #f5f5f5;
    }
    .inv_container {
      background: white;
      padding: 30px;
      border: 1px solid #ccc;
      width: 100%;
      max-width: 800px;
      margin: auto;
      box-shadow: 0 0 5px rgba(0,0,0,0.1);
    }
    .inv_table {
      border-collapse: collapse;
      width: 100%;
      margin-top: 1rem;
    }
    .inv_th, .inv_td {
      border: 1px solid #ccc;
      padding: 8px;
      text-align: left;
      font-size: 14px;
    }
    .inv_th {
      background-color: #f0f0f0;
      font-weight: bold;
    }
    .inv_h1, .inv_h2, .inv_h3, .inv_h4 {
      margin: 4px 0;
    }
    .inv_dotted_line {
      border-top: 1px dotted #333;
      margin: 2rem 0;
    }
    .inv_flex {
      display: flex;
      justify-content: space-between;
    }
  </style>
</head>
<body>
  <div class="inv_container">

    <h1 style="font-size:18px; font-weight:bold; text-align:center; margin-bottom:10px;">
      ${order.locationName || "TAX INVOICE"}
    </h1>

    <div class="inv_flex">
      <div style="width:60%;">
        <h2 style="margin:5px 0;">${company.name}</h2>
        <p style="margin:3px 0;">${company.address}</p>
        <p style="margin:3px 0;">${company.city}, ${company.state}</p>
        <p><strong>Email:</strong> ${company.email}</p>
        <p><strong>GST No:</strong> ${company.gstNumber}</p>
        <p><strong>Phone:</strong> ${company.mobileNumber}</p>
      </div>

      <div style="width:35%; text-align:right;">
        ${right.map(itm => `
          <p><strong>${itm.field}:</strong> ${itm.value}</p>
        `).join("")}
      </div>
    </div>

    <div class="inv_dotted_line"></div>

    <div class="inv_flex">
      <div style="width:48%;">
        <h3>Bill To</h3>
        ${bodyLeftSection.map(itm => `<p>${itm.field}</p>`).join("")}
      </div>
      <div style="width:48%;">
        <h3>Ship To</h3>
        ${bodyRightSection.map(itm => `<p>${itm.field}</p>`).join("")}
      </div>
    </div>

    <div class="inv_dotted_line"></div>

    <!-- Improved Table Matching Your React Design -->
    <table class="inv_table">
      <thead>
        <tr>
          ${isDeliveredNonSwatch ? `<th class="inv_th" style="width:6%;">S.No</th>` : ""}
          <th class="inv_th">Product Name</th>
          <th class="inv_th">${isWholesale ? "Rolls" : "Qty"}</th>
          <th class="inv_th">UOM</th>
          ${isWholesale ? `<th class="inv_th">Qty/Roll</th>` : ""}
          <th class="inv_th">Sell Price/Qty</th>
          <th class="inv_th">Total Amt.</th>
        </tr>
      </thead>
      <tbody>
        ${tableRows}
      </tbody>
    </table>

    <!-- Summary Section (same as before, slightly cleaned) -->
    <div style="margin-top: 30px; display: flex; justify-content: space-between; gap: 20px;">
      <div style="width: 55%; font-size: 13px;">
        <h4>1. Total Payment Due in 30 Days</h4>
        <h4>2. Please include the invoice number on your check</h4>
        <h4>If you have any questions, please contact:</h4>
        <p><strong>Name:</strong> ${order?.customer?.name || "N/A"}</p>
        <p><strong>E-Mail:</strong> ${order?.customer?.email || "N/A"}</p>
        <p><strong>Phone:</strong> ${order?.customer?.mobileNo || "N/A"}</p>
      </div>

      <div style="width: 40%;">
        <div class="inv_flex"><p><strong>Total (Product):</strong></p><p>₹ ${grossTotal}</p></div>
        <div class="inv_flex"><p><strong>Packing Charges:</strong></p><p>₹ ${(order.packingCharge || 0).toFixed(2)}</p></div>
        <div class="inv_flex"><p><strong>Shipment Charges:</strong></p><p>₹ ${(order.shipmentCost || 0).toFixed(2)}</p></div>
        <div class="inv_flex"><p><strong>IGST:</strong></p><p>₹ ${(order.igst || 0).toFixed(2)}</p></div>
        <div class="inv_flex"><p><strong>CGST:</strong></p><p>₹ ${(order.totalGst / 2 || 0).toFixed(2)}</p></div>
        <div class="inv_flex"><p><strong>SGST:</strong></p><p>₹ ${(order.totalGst / 2 || 0).toFixed(2)}</p></div>
        
        <div class="inv_flex">
          <p><strong>Total Amt:</strong></p>
          <p>₹ ${parseFloat(order?.grossTotal || 0).toFixed(2)}</p>
        </div>
        
        <div class="inv_flex"><p><strong>Round Off:</strong></p><p>₹ ${(order.adjustedAmount || 0).toFixed(2)}</p></div>
        
        <div class="inv_flex" style="font-weight:bold; font-size:15px;border-top:1px solid #ccc; padding-top:8px;">
          <p><strong>Total Amt (Rounded Off):</strong></p>
          <p>₹ ${Math.round(parseFloat(order.grossTotal || 0) + parseFloat(order.adjustedAmount || 0)).toFixed(2)}</p>
        </div>
      </div>
    </div>

    <div style="text-align: center; font-weight: bold; margin-top: 40px; font-size: 16px;">
      Thanks For Your Business !!!
    </div>

  </div>
</body>
</html>
`;
};

// Rest of your code (generateAndDownloadPDF) remains the same
const B2B_API = createB2BAPI();

export const generateAndDownloadPDF = async (order) => {
  let company;
  const fetchCompany = async () => {
    const response = await B2B_API.get(`company/${order?.companyId}`).json();
    company = response?.response;
  };
  await fetchCompany();

  const html = generateInvoiceHTML(order, company);
  const fileName = `Invoice_${order?.invoice?.invoiceNo || "N/A"}.pdf`;

  const opt = {
    margin: [0.4, 0.4, 0.4, 0.4],
    filename: fileName,
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: { scale: 2, useCORS: true },
    jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
  };

  html2pdf().from(html).set(opt).save();
};